import { InjectionToken, Signal, signal } from '@angular/core';

import { EMPTY, Observable } from 'rxjs';

/**
 * Evento entregue por um canal de tempo real.
 *
 * `version` é a sequência monotônica do agregado no servidor. É ela que permite
 * descartar evento repetido, ignorar evento fora de ordem e detectar buraco —
 * sem isso não há como saber se a tela está sincronizada.
 */
export interface RealtimeEvent<T = unknown> {
  /** Nome do evento no domínio, ex.: `item.lancado`. */
  readonly type: string;
  /** Sequência monotônica do agregado, atribuída pelo servidor. */
  readonly version: number;
  readonly payload: T;
}

/**
 * Contrato de transporte. O store nunca conhece a implementação — troca de
 * WebSocket para qualquer outra coisa não toca em nenhum store.
 *
 * ⚠️ O `channel` é **filtro do lado do cliente**, nunca instrução para o servidor
 * (F7.1/D59): quem decide o que um socket ouve é o token, no handshake. Cliente
 * que peça um canal a que não tem direito simplesmente não recebe nada.
 */
export interface RealtimeTransport {
  connect<T>(channel: string): Observable<RealtimeEvent<T>>;

  /**
   * Conexão viva agora.
   *
   * Existe porque quem consome realtime também tem um caminho de reserva — o
   * painel de pedidos estica o poll de 15s para 60s enquanto o socket está de pé
   * e volta a 15s quando ele cai. Sem esse sinal, ou se paga polling agressivo
   * com a conexão viva, ou se fica cego quando ela morre.
   *
   * Opcional para transporte que não tem conexão nenhuma (o Noop).
   */
  readonly connected?: Signal<boolean>;
}

/**
 * Transporte padrão: nunca emite.
 *
 * É o que fica registrado enquanto nenhum transporte real é provido, para que
 * store que usa `withRealtime` funcione normalmente sem conexão nenhuma.
 */
export class NoopRealtimeTransport implements RealtimeTransport {
  /** Nunca conecta — e é isso que mantém o consumidor no caminho de reserva. */
  readonly connected = signal(false).asReadonly();

  connect<T>(): Observable<RealtimeEvent<T>> {
    return EMPTY;
  }
}

export const REALTIME_TRANSPORT = new InjectionToken<RealtimeTransport>('REALTIME_TRANSPORT', {
  providedIn: 'root',
  factory: () => new NoopRealtimeTransport(),
});
